export type CreditPlan = 'free' | 'pro' | 'team';
export type CreditOperation = 'chat' | 'image' | 'video' | 'model3d' | 'render' | 'audio' | 'app-gateway';
export type CreditProvider = 'openrouter' | 'fal' | 'hyperframes' | 'remotion' | 'internal';

export interface BillableModel {
  id: string;
  externalModelId: string;
  label: string;
  provider: CreditProvider;
  operation: CreditOperation;
  rawCostUsd: number;
  unit: 'request' | 'image' | 'video-second' | 'generation' | 'render-second';
  notes?: string;
  maxReferences?: number;
  supportsReferenceImages?: boolean;
  supportsTransparency?: boolean;
  supportsAudio?: boolean;
  requiresSourceImage?: boolean;
  videoMode?: 'text-to-video' | 'image-to-video' | 'reference-to-video';
  defaultDurationSeconds?: number;
  maxDurationSeconds?: number;
  supportedAspectRatios?: string[];
  supportedResolutions?: string[];
  latency?: string;
  isProOnly?: boolean;
  proReason?: string;
  defaultAspectRatio?: string;
  resolutionMultipliers?: Record<string, number>;
}

export interface CreditEstimateInput {
  operation: CreditOperation;
  modelId?: string;
  quantity?: number;
  providerCostUsd?: number;
  providerCostMicrodollars?: number;
  durationSeconds?: number;
  width?: number;
  height?: number;
  resolution?: '0.5K' | '1K' | '2K' | '4K';
  texturedMesh?: boolean;
}

export interface CreditEstimate {
  credits: number;
  rawCostUsd: number;
  providerCostMicrodollars: number;
  quantity: number;
  model?: BillableModel;
  notes?: string;
}

export const CREDIT_EXCHANGE = {
  // One credit represents roughly one cent of raw provider spend before the
  // app's multiplier. Keep this in code for now so pricing can be reviewed
  // in one place before moving it to admin-controlled config.
  rawUsdPerCredit: 0.01,
  providerCostMultiplier: 1.6,
  minBillableCredits: 1,
} as const;

export const MONTHLY_CREDIT_GRANTS: Record<CreditPlan, number> = {
  free: 150,
  pro: 3000,
  team: 10000,
};

export const CREDIT_PACKS = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 500,
    unitAmountCents: 700,
    description: 'Small top-up for quick experiments',
  },
  {
    id: 'creator',
    name: 'Creator',
    credits: 2000,
    unitAmountCents: 2200,
    description: 'Best fit for image batches and short video tests',
  },
  {
    id: 'studio',
    name: 'Studio',
    credits: 6000,
    unitAmountCents: 5500,
    description: 'Bulk credits for heavier media work',
  },
] as const;

export type CreditPackId = (typeof CREDIT_PACKS)[number]['id'];

export const BILLABLE_MODELS: Record<string, BillableModel> = {
  // OpenRouter image endpoints. OpenRouter exposes current prices through
  // /api/v1/models, and provider usage can override these estimates when
  // the response includes an exact cost.
  'gemini-3-pro': {
    id: 'gemini-3-pro',
    externalModelId: 'google/gemini-3-pro-image-preview',
    label: 'Gemini 3 Pro Image',
    provider: 'openrouter',
    operation: 'image',
    rawCostUsd: 0.15,
    unit: 'image',
    maxReferences: 8,
    supportsReferenceImages: true,
    isProOnly: true,
    proReason: 'Premium image reasoning model',
    notes: 'Premium image reasoning path via OpenRouter.',
  },
  'gemini-3.1-flash': {
    id: 'gemini-3.1-flash',
    externalModelId: 'google/gemini-3.1-flash-image-preview',
    label: 'Gemini 3.1 Flash Image',
    provider: 'openrouter',
    operation: 'image',
    rawCostUsd: 0.04,
    unit: 'image',
    maxReferences: 8,
    supportsReferenceImages: true,
    notes: 'Fast general image generation via OpenRouter.',
  },
  'gemini-2.5-flash': {
    id: 'gemini-2.5-flash',
    externalModelId: 'google/gemini-2.5-flash-image',
    label: 'Gemini 2.5 Flash Image',
    provider: 'openrouter',
    operation: 'image',
    rawCostUsd: 0.04,
    unit: 'image',
    maxReferences: 8,
    supportsReferenceImages: true,
  },
  'gpt-image-2': {
    id: 'gpt-image-2',
    externalModelId: 'openai/gpt-5.4-image-2',
    label: 'GPT Image 2',
    provider: 'openrouter',
    operation: 'image',
    rawCostUsd: 0.08,
    unit: 'image',
    isProOnly: true,
    proReason: 'Premium OpenAI image model',
    notes: 'Estimate until exact OpenRouter usage metadata is available.',
  },

  // fal image endpoints.
  'fal/sana-sprint': {
    id: 'fal/sana-sprint',
    externalModelId: 'fal-ai/sana/sprint',
    label: 'SANA Sprint',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.003,
    unit: 'image',
    notes: 'Fast low-cost draft image model.',
  },
  'fal/flux-schnell': {
    id: 'fal/flux-schnell',
    externalModelId: 'fal-ai/flux/schnell',
    label: 'FLUX Schnell',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.003,
    unit: 'image',
  },
  'fal/flux-2-flash': {
    id: 'fal/flux-2-flash',
    externalModelId: 'fal-ai/flux-2/flash',
    label: 'FLUX 2 Flash',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.006,
    unit: 'image',
  },
  'fal/flux-dev': {
    id: 'fal/flux-dev',
    externalModelId: 'fal-ai/flux/dev',
    label: 'FLUX Dev',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.025,
    unit: 'image',
  },
  'fal/nano-banana': {
    id: 'fal/nano-banana',
    externalModelId: 'fal-ai/nano-banana',
    label: 'Nano Banana',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.039,
    unit: 'image',
    maxReferences: 14,
    supportsReferenceImages: true,
    supportsTransparency: true,
  },
  'fal/nano-banana-2': {
    id: 'fal/nano-banana-2',
    externalModelId: 'fal-ai/nano-banana-2',
    label: 'Nano Banana 2',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.039,
    unit: 'image',
    maxReferences: 14,
    supportsReferenceImages: true,
    supportsTransparency: true,
    resolutionMultipliers: {
      '0.5K': 0.75,
      '1K': 1,
      '2K': 1.5,
      '4K': 2,
    },
    notes: 'Resolution multipliers follow fal model docs.',
  },
  'fal/nano-banana-pro': {
    id: 'fal/nano-banana-pro',
    externalModelId: 'fal-ai/nano-banana-pro',
    label: 'Nano Banana Pro',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.15,
    unit: 'image',
    maxReferences: 14,
    supportsReferenceImages: true,
    supportsTransparency: true,
    isProOnly: true,
    proReason: 'Premium Nano Banana Pro model',
  },
  'fal/ideogram-v3': {
    id: 'fal/ideogram-v3',
    externalModelId: 'fal-ai/ideogram/v3',
    label: 'Ideogram v3',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.08,
    unit: 'image',
    isProOnly: true,
    proReason: 'Typography-focused premium model',
    notes: 'Typography-focused image model.',
  },
  'fal/flux-kontext-pro': {
    id: 'fal/flux-kontext-pro',
    externalModelId: 'fal-ai/flux-pro/kontext',
    label: 'FLUX Kontext Pro',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.04,
    unit: 'image',
    maxReferences: 1,
    supportsReferenceImages: true,
  },
  'fal/flux-kontext-max': {
    id: 'fal/flux-kontext-max',
    externalModelId: 'fal-ai/flux-pro/kontext/max/multi',
    label: 'FLUX Kontext Max',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.08,
    unit: 'image',
    maxReferences: 4,
    supportsReferenceImages: true,
    isProOnly: true,
    proReason: 'Premium multi-reference edit model',
  },
  'fal/seedream-v5-lite': {
    id: 'fal/seedream-v5-lite',
    externalModelId: 'fal-ai/bytedance/seedream/v5/lite/text-to-image',
    label: 'Seedream v5 Lite',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.025,
    unit: 'image',
    maxReferences: 10,
    supportsReferenceImages: true,
  },
  'fal/seedream-v45-edit': {
    id: 'fal/seedream-v45-edit',
    externalModelId: 'fal-ai/bytedance/seedream/v4.5/edit',
    label: 'Seedream v4.5 Edit',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.04,
    unit: 'image',
    maxReferences: 10,
    supportsReferenceImages: true,
  },
  'fal/flux-2-pro-edit': {
    id: 'fal/flux-2-pro-edit',
    externalModelId: 'fal-ai/flux-2-pro/edit',
    label: 'FLUX 2 Pro Edit',
    provider: 'fal',
    operation: 'image',
    rawCostUsd: 0.06,
    unit: 'image',
    maxReferences: 9,
    supportsReferenceImages: true,
    isProOnly: true,
    proReason: 'Premium edit model',
  },

  // 3D.
  'fal/hunyuan3d-v2-mini-turbo': {
    id: 'fal/hunyuan3d-v2-mini-turbo',
    externalModelId: 'fal-ai/hunyuan3d/v2/mini/turbo',
    label: 'Hunyuan3D v2 Mini Turbo',
    provider: 'fal',
    operation: 'model3d',
    rawCostUsd: 0.08,
    unit: 'generation',
    isProOnly: true,
    proReason: '3D asset generation',
    notes: 'Image-to-3D GLB. Textured mesh is billed at 3x by fal.',
  },

  // fal video endpoints. IDs are app-facing and map to FAL queue slugs.
  'fal/ltx-2-19b-t2v': {
    id: 'fal/ltx-2-19b-t2v',
    externalModelId: 'fal-ai/ltx-2-19b/text-to-video',
    label: 'LTX-2 19B Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.045,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: true,
    defaultDurationSeconds: 6,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    supportedResolutions: ['720p', '1080p'],
    latency: '30-60s',
    notes: 'Fast low-cost video with audio. FAL bills by MP-frame; this is a conservative per-second estimate.',
  },
  'fal/wan-25-t2v': {
    id: 'fal/wan-25-t2v',
    externalModelId: 'fal-ai/wan-25-preview/text-to-video',
    label: 'WAN 2.5 Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.05,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: false,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    supportedResolutions: ['720p', '1080p'],
    latency: '20-60s',
    notes: 'Balanced text-to-video draft model.',
  },
  'fal/seedance-2-fast-t2v': {
    id: 'fal/seedance-2-fast-t2v',
    externalModelId: 'bytedance/seedance-2.0/fast/text-to-video',
    label: 'Seedance 2.0 Fast Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.08,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 12,
    supportedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    supportedResolutions: ['720p', '1080p'],
    latency: '30-90s',
    notes: 'Fast cinematic text-to-video path.',
  },
  'fal/kling-v3-4k-t2v': {
    id: 'fal/kling-v3-4k-t2v',
    externalModelId: 'fal-ai/kling-video/v3/4k/text-to-video',
    label: 'Kling 3.0 4K Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.22,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: false,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 15,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['1080p', '4K'],
    latency: '60-180s',
    isProOnly: true,
    proReason: 'Premium 4K video model',
    notes: 'Premium high-resolution motion model.',
  },
  'fal/veo-3.1-t2v': {
    id: 'fal/veo-3.1-t2v',
    externalModelId: 'fal-ai/veo3.1',
    label: 'Veo 3.1 Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.4,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16'],
    supportedResolutions: ['720p', '1080p', '4K'],
    latency: '120-240s',
    isProOnly: true,
    proReason: 'Premium Veo model',
    notes: 'Premium cinematic model with native audio.',
  },
  'fal/veo-3.1-fast-t2v': {
    id: 'fal/veo-3.1-fast-t2v',
    externalModelId: 'fal-ai/veo3.1/fast/text-to-video',
    label: 'Veo 3.1 Fast Text-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.15,
    unit: 'video-second',
    videoMode: 'text-to-video',
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16'],
    supportedResolutions: ['720p', '1080p', '4K'],
    latency: '60-120s',
    isProOnly: true,
    proReason: 'Premium Veo model',
    notes: 'Faster Veo path for iteration.',
  },
  'fal/ltx-2-19b-i2v': {
    id: 'fal/ltx-2-19b-i2v',
    externalModelId: 'fal-ai/ltx-2-19b/image-to-video',
    label: 'LTX-2 19B Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.045,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    defaultDurationSeconds: 6,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    supportedResolutions: ['720p', '1080p'],
    latency: '30-60s',
    notes: 'Cheap image-to-video motion pass.',
  },
  'fal/seedance-2-fast-i2v': {
    id: 'fal/seedance-2-fast-i2v',
    externalModelId: 'bytedance/seedance-2.0/fast/image-to-video',
    label: 'Seedance 2.0 Fast Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.08,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 12,
    supportedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    supportedResolutions: ['720p', '1080p'],
    latency: '30-90s',
  },
  'fal/seedance-2-ref': {
    id: 'fal/seedance-2-ref',
    externalModelId: 'bytedance/seedance-2.0/reference-to-video',
    label: 'Seedance 2.0 Reference-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.1,
    unit: 'video-second',
    videoMode: 'reference-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    supportsReferenceImages: true,
    maxReferences: 4,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 12,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['720p', '1080p'],
    latency: '60-120s',
    notes: 'Reference-guided identity/style video.',
  },
  'fal/kling-v3-pro-i2v': {
    id: 'fal/kling-v3-pro-i2v',
    externalModelId: 'fal-ai/kling-video/v3/pro/image-to-video',
    label: 'Kling 3.0 Pro Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.16,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 15,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['720p', '1080p'],
    latency: '60-120s',
    isProOnly: true,
    proReason: 'Premium image-to-video model',
  },
  'fal/kling-v3-4k-i2v': {
    id: 'fal/kling-v3-4k-i2v',
    externalModelId: 'fal-ai/kling-video/v3/4k/image-to-video',
    label: 'Kling 3.0 4K Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.24,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 15,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['1080p', '4K'],
    latency: '60-180s',
    isProOnly: true,
    proReason: 'Premium 4K video model',
  },
  'fal/kling-o3-ref': {
    id: 'fal/kling-o3-ref',
    externalModelId: 'fal-ai/kling-video/o3/4k/reference-to-video',
    label: 'Kling O3 4K Reference-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.24,
    unit: 'video-second',
    videoMode: 'reference-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    supportsReferenceImages: true,
    maxReferences: 9,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 15,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['1080p', '4K'],
    latency: '90-180s',
    isProOnly: true,
    proReason: 'Premium reference video model',
  },
  'fal/pixverse-v6-i2v': {
    id: 'fal/pixverse-v6-i2v',
    externalModelId: 'fal-ai/pixverse/v6/image-to-video',
    label: 'PixVerse v6 Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.08,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: false,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16', '1:1'],
    supportedResolutions: ['720p', '1080p'],
    latency: '30-90s',
  },
  'fal/veo-3.1-fast-i2v': {
    id: 'fal/veo-3.1-fast-i2v',
    externalModelId: 'fal-ai/veo3.1/fast/image-to-video',
    label: 'Veo 3.1 Fast Image-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.15,
    unit: 'video-second',
    videoMode: 'image-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16'],
    supportedResolutions: ['720p', '1080p', '4K'],
    latency: '90-180s',
    isProOnly: true,
    proReason: 'Premium Veo model',
  },
  'fal/veo-3.1-fast-ref': {
    id: 'fal/veo-3.1-fast-ref',
    externalModelId: 'fal-ai/veo3.1/fast/reference-to-video',
    label: 'Veo 3.1 Fast Reference-to-Video',
    provider: 'fal',
    operation: 'video',
    rawCostUsd: 0.15,
    unit: 'video-second',
    videoMode: 'reference-to-video',
    requiresSourceImage: true,
    supportsAudio: true,
    supportsReferenceImages: true,
    maxReferences: 4,
    defaultDurationSeconds: 5,
    maxDurationSeconds: 8,
    supportedAspectRatios: ['16:9', '9:16'],
    supportedResolutions: ['720p', '1080p', '4K'],
    latency: '90-180s',
    isProOnly: true,
    proReason: 'Premium Veo model',
  },

  // Rendering fallback estimates.
  'hyperframes/mp4': {
    id: 'hyperframes/mp4',
    externalModelId: 'hyperframes/mp4',
    label: 'HyperFrames MP4 Render',
    provider: 'hyperframes',
    operation: 'render',
    rawCostUsd: 0.002,
    unit: 'render-second',
  },
  'remotion/lambda-mp4': {
    id: 'remotion/lambda-mp4',
    externalModelId: 'remotion/lambda-mp4',
    label: 'Remotion Lambda MP4 Render',
    provider: 'remotion',
    operation: 'render',
    rawCostUsd: 0.002,
    unit: 'render-second',
  },
} as const;

export const IMAGE_BILLABLE_MODELS = Object.values(BILLABLE_MODELS)
  .filter((model) => model.operation === 'image');

export const MODEL3D_BILLABLE_MODELS = Object.values(BILLABLE_MODELS)
  .filter((model) => model.operation === 'model3d');

export const VIDEO_BILLABLE_MODELS = Object.values(BILLABLE_MODELS)
  .filter((model) => model.operation === 'video');

export function getCreditPack(packId: string | null | undefined) {
  if (!packId) return undefined;
  return CREDIT_PACKS.find((pack) => pack.id === packId);
}

export function getPlanMonthlyCredits(plan: string | null | undefined): number {
  if (plan === 'team') return MONTHLY_CREDIT_GRANTS.team;
  if (plan === 'pro') return MONTHLY_CREDIT_GRANTS.pro;
  return MONTHLY_CREDIT_GRANTS.free;
}

export function getCreditPeriodKey(date = new Date()): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function creditsFromUsd(rawCostUsd: number, minCredits = CREDIT_EXCHANGE.minBillableCredits): number {
  if (!Number.isFinite(rawCostUsd) || rawCostUsd <= 0) {
    return minCredits > 0 ? minCredits : 0;
  }

  const rawCredits =
    (rawCostUsd * CREDIT_EXCHANGE.providerCostMultiplier) / CREDIT_EXCHANGE.rawUsdPerCredit;

  return Math.max(minCredits, Math.ceil(rawCredits - 1e-9));
}

export function usdToMicrodollars(rawCostUsd: number): number {
  if (!Number.isFinite(rawCostUsd) || rawCostUsd <= 0) return 0;
  return Math.round(rawCostUsd * 1_000_000);
}

export function microdollarsToUsd(value: number | null | undefined): number {
  if (!value || value <= 0) return 0;
  return value / 1_000_000;
}

export function normalizeImageResolution(input: Pick<CreditEstimateInput, 'resolution' | 'width' | 'height'>): '0.5K' | '1K' | '2K' | '4K' {
  if (input.resolution) return input.resolution;
  const longest = Math.max(input.width ?? 1024, input.height ?? 1024);
  if (longest <= 768) return '0.5K';
  if (longest <= 1536) return '1K';
  if (longest <= 3072) return '2K';
  return '4K';
}

export interface ProOnlyMediaFeature {
  id: 'premium_model' | 'high_resolution' | 'transparent_background' | 'reference_images' | 'model3d_generation' | 'textured_mesh';
  label: string;
  reason: string;
}

function shouldGateFeature(featureId: FeatureId) {
  return isFeaturePlanGated(featureId);
}

export function getProOnlyMediaFeatures(input: CreditEstimateInput & {
  transparent?: boolean;
  referenceImageCount?: number;
}): ProOnlyMediaFeature[] {
  const features: ProOnlyMediaFeature[] = [];
  const model = input.modelId ? BILLABLE_MODELS[input.modelId] : undefined;

  if (model?.isProOnly && shouldGateFeature(model.operation === 'model3d' ? 'media.model3d.generation' : 'media.premiumModels')) {
    features.push({
      id: model.operation === 'model3d' ? 'model3d_generation' : 'premium_model',
      label: model.operation === 'model3d' ? '3D generation' : 'Premium model',
      reason: model.proReason ?? 'This model is available on Pro.',
    });
  }

  if (input.operation === 'image') {
    const resolution = normalizeImageResolution(input);
    if ((resolution === '2K' || resolution === '4K') && shouldGateFeature('media.image.highResolution')) {
      features.push({
        id: 'high_resolution',
        label: `${resolution} output`,
        reason: 'High-resolution generations are Pro-only.',
      });
    }

    if (input.transparent && shouldGateFeature('media.image.transparentBackground')) {
      features.push({
        id: 'transparent_background',
        label: 'Transparent PNG',
        reason: 'Transparent-background exports are Pro-only.',
      });
    }

    if ((input.referenceImageCount ?? 0) > 0 && shouldGateFeature('media.image.referenceImages')) {
      features.push({
        id: 'reference_images',
        label: 'Reference images',
        reason: 'Reference-image and consistency workflows are Pro-only.',
      });
    }
  }

  if (input.operation === 'model3d' && input.texturedMesh && shouldGateFeature('media.model3d.texturedMesh')) {
    features.push({
      id: 'textured_mesh',
      label: 'Textured mesh',
      reason: 'Textured 3D meshes are Pro-only.',
    });
  }

  const seen = new Set<string>();
  return features.filter((feature) => {
    if (seen.has(feature.id)) return false;
    seen.add(feature.id);
    return true;
  });
}

export function estimateOperationCredits(input: CreditEstimateInput): CreditEstimate {
  const quantity = Math.max(1, input.quantity ?? 1);
  const model = input.modelId ? BILLABLE_MODELS[input.modelId] : undefined;

  let rawCostUsd =
    typeof input.providerCostMicrodollars === 'number'
      ? microdollarsToUsd(input.providerCostMicrodollars)
      : input.providerCostUsd;

  if (rawCostUsd === undefined) {
    if (model?.unit === 'video-second' || model?.unit === 'render-second') {
      rawCostUsd = model.rawCostUsd * Math.max(1, input.durationSeconds ?? 1);
    } else {
      rawCostUsd = model?.rawCostUsd ?? 0.01;
    }
  }

  if (input.operation === 'image') {
    const resolution = normalizeImageResolution(input);
    const multiplier = model?.resolutionMultipliers?.[resolution] ?? 1;
    rawCostUsd *= multiplier;
  }

  if (input.operation === 'model3d' && input.texturedMesh) {
    rawCostUsd *= 3;
  }

  rawCostUsd *= quantity;

  return {
    credits: creditsFromUsd(rawCostUsd),
    rawCostUsd,
    providerCostMicrodollars: usdToMicrodollars(rawCostUsd),
    quantity,
    model,
    notes: model?.notes,
  };
}
import { isFeaturePlanGated, type FeatureId } from '@/lib/feature-access';
